<template>
  <div>
    <loading-spinner v-if="!store" />
    <app-header v-if="store" :title="originalName ? editTitle : newTitle">
      <template #col-right>
        <b-button variant="primary" class="mr-1" @click="save">
          <span v-label-key="'SAVE_CHANGES'">Save Changes</span>
        </b-button>
        <cancel-button />
      </template>
    </app-header>

    <b-container
      v-if="store && distributors && subdistributors && chains && countries"
    >
      <b-row>
        <b-col md="6">
          <errors v-if="errors.length" :errors="errors" />
          <form ref="editorForm">
            <h3 v-label-key="'EDIT_CORE_INFORMATION_TITLE'">
              Core Information
            </h3>
            <b-form-group
              :label="`${getLabel('DISTRIBUTOR')} *`"
              :description="getLabel('EDIT_STORE_DISTRIBUTOR_DESCRIPTION')"
            >
              <b-form-select
                v-model="store.distributor_id"
                class="form-control"
                required
              >
                <b-form-select-option :value="null" disabled>
                  <span v-label-key="'PLEASE_SELECT_OPTION'"
                    >Please select an option</span
                  >
                </b-form-select-option>
                <b-form-select-option
                  v-for="distributor in distributors"
                  :key="distributor.id"
                  :value="distributor.id"
                >
                  {{ distributor.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="getLabel('SUBDISTRIBUTOR_OPTIONAL')"
              :description="getLabel('EDIT_STORE_SUBDISTRIBUTOR_DESCRIPTION')"
            >
              <b-form-select v-model="store.subdistributor_id">
                <b-form-select-option :value="null">
                  {{
                    !store.distributor_id
                      ? getLabel('SELECT_DISTRIBUTOR_FIRST')
                      : getLabel('NO_SUBDISTRIBUTOR')
                  }}
                </b-form-select-option>
                <b-form-select-option
                  v-for="subdistributor in displaySubdistributors"
                  :key="subdistributor.id"
                  :value="subdistributor.id"
                >
                  {{ subdistributor.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('CHAIN')} *`"
              :description="getLabel('EDIT_STORE_CHAIN_DESCRIPTION')"
            >
              <b-form-select v-model="store.chain_id" required>
                <b-form-select-option :value="null" disabled>
                  {{
                    !store.distributor_id
                      ? getLabel('SELECT_DISTRIBUTOR_SUBDISTRIBUTOR_FIRST')
                      : getLabel('PLEASE_SELECT_OPTION')
                  }}
                </b-form-select-option>
                <b-form-select-option
                  v-for="chain in displayChains"
                  :key="chain.id"
                  :value="chain.id"
                >
                  {{ chain.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('NAME')} *`"
              :description="getLabel('EDIT_STORE_STORE_DESCRIPTION')"
            >
              <b-form-input v-model="store.name" type="text" required />
            </b-form-group>

            <label class="form-check">
              <input
                v-model="store.active"
                type="checkbox"
                class="form-check-input"
              />
              <span
                class="form-check-label"
                v-label-key="'STORE_ACTIVE_DESCRIPTION'"
                >Active (Allows the store to use their Retail app to perform
                cuts)</span
              >
            </label>

            <br />
            <h3 v-label-key="'STORE_LOGIN_DETAILS_HEADER'">Login Details</h3>
            <b-form-group
              :label="getLabel('STORE_LOGIN_USERNAME')"
              :description="
                `${getLabel('STORE_LOGIN_USERNAME_DESCRIPTION')} ${getLabel(
                  'STORE_LOGIN_USERNAME_DESCRIPTION_2'
                )}`
              "
            >
              <b-form-input v-model="store.username" type="text" />
            </b-form-group>

            <b-form-group
              :label="`${getLabel('STORE_LOGIN_PASSWORD')} *`"
              :description="getLabel('STORE_LOGIN_PASSWORD_DESCRIPTION')"
            >
              <b-form-input v-model="store.password" type="text" required />
            </b-form-group>

            <br />
            <h3 v-label-key="'LOCATION_STORE_ADDRESS'">Store Address</h3>
            <b-form-group :label="getLabel('LOCATION_STREET')">
              <b-form-input v-model="store.street" type="text" />
            </b-form-group>

            <b-form-group :label="getLabel('LOCATION_CITY')">
              <b-form-input v-model="store.city" type="text" />
            </b-form-group>

            <b-form-group :label="getLabel('LOCATION_REGION')">
              <b-form-input v-model="store.region" type="text" />
            </b-form-group>

            <b-form-group :label="getLabel('LOCATION_POSTCODE')">
              <b-form-input v-model="store.postcode" type="text" />
            </b-form-group>

            <b-form-group
              :label="`${getLabel('LOCATION_COUNTRY')} *`"
              :description="getLabel('LOCATION_COUNTRY_DESCRIPTION')"
            >
              <b-form-select v-model="store.country" type="text" required>
                <b-form-select-option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="getLabel('EDIT_STORE_PHONE_NUMBER')"
              :description="getLabel('EDIT_STORE_PHONE_NUMBER_DESCRIPTION')"
            >
              <b-form-input v-model="store.contact_number" type="tel" />
            </b-form-group>

            <b-form-group
              :label="getLabel('EDIT_STORE_EMAIL_ADDRESS')"
              :description="getLabel('EDIT_STORE_EMAIL_ADDRESS_DESCRIPTION')"
            >
              <b-form-input v-model="store.notification_email" type="email" />
            </b-form-group>

            <b-form-group
              :label="getLabel('OPENING_HOURS')"
              :description="getLabel('EDIT_STORE_OPENING_HOURS_DESCRIPTION')"
            >
              <b-form-textarea v-model="store.opening_hours" />
            </b-form-group>

            <br />
            <!-- Retail Support -->
            <h3 v-label-key="'RETAIL_APP_SUPPORT_DETAILS'">
              Retail App Support Details
            </h3>
            <p v-label-key="'RETAIL_APP_SUPPORT_DETAILS_DESCRIPTION'">
              These details are for retail app users to know who they can
              contact if they encounter issues with the app
            </p>
            <b-form-group :label="getLabel('STORE_SETTING_SET_SUPPORT_NAME')">
              <b-form-input v-model="store.support_name" type="text" />
            </b-form-group>

            <b-form-group :label="getLabel('STORE_SETTING_SET_SUPPORT_EMAIL')">
              <b-form-input v-model="store.support_email" type="email" />
            </b-form-group>

            <b-form-group :label="getLabel('STORE_SETTING_SET_SUPPORT_PHONE')">
              <b-form-input v-model="store.support_phone" type="tel" />
            </b-form-group>

            <br />
            <h3 v-label-key="'OTHER'">Other</h3>
            <!-- Chain Group -->
            <b-form-group
              :label="getLabel('STORE_SETTING_SET_CHAIN_GROUP')"
              :description="
                getLabel('STORE_SETTING_SET_CHAIN_GROUP_DESCRIPTION')
              "
            >
              <b-form-input v-model="store.chain_group" type="text" />
            </b-form-group>
            <label class="form-check">
              <input
                v-model="store.hidden"
                type="checkbox"
                class="form-check-input"
              />
              <span
                class="form-check-label"
                v-label-key="'EDIT_STORE_HIDDEN_DESCRIPTION'"
                >Hidden (may hide the store from customers)</span
              >
            </label>
            <label
              v-if="selectedChain && selectedChain.enable_register_fourseasons"
              class="form-check"
            >
              <input
                v-model="store.enable_fourseasons"
                type="checkbox"
                class="form-check-input"
              />
              <span
                class="form-check-label"
                v-label-key="'STORE_SETTING_ENABLE_HAS_FOUR_SEASONS_PRINTER'"
                >Has Four Seasons Printer</span
              >
            </label>

            <!-- <div class="form-group">
              <label>Four Seasons Films Available</label>
              <input type="number" class="form-control" v-model="store.fsfilms_available" />
            </div> -->
          </form>
        </b-col>
        <b-col md="7" />
      </b-row>
    </b-container>
  </div>
</template>
<script>
import $auth from '@/services/auth';
import $api from '@/services/api';
import CancelButton from '@/components/CancelButton';
import LoadingSpinner from '@/components/LoadingSpinner';
import Errors from '@/components/Errors';
import AppHeader from '@/components/AppHeader';
import { mapGetters } from 'vuex';

export default {
  components: {
    CancelButton,
    LoadingSpinner,
    Errors,
    AppHeader,
  },
  data() {
    return {
      auth: $auth,
      originalName: null,
      store: null,
      distributors: null,
      subdistributors: null,
      chains: null,
      countries: null,
      errors: [],
    };
  },

  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
      getReplaceLabel: 'getReplaceLabel',
    }),
    editTitle() {
      return this.getReplaceLabel('EDIT_ITEM_NAME', {
        '{NAME}': this.originalName,
      });
    },
    newTitle() {
      return this.getReplaceLabel('NEW_ITEM_NAME', {
        '{NAME}': this.getLabel('STORE'),
      });
    },
    displaySubdistributors() {
      return this.subdistributors.filter(
        subdistributor =>
          subdistributor.distributor_id === this.store.distributor_id
      );
    },

    displayChains() {
      return this.chains.filter(
        chain =>
          chain.subdistributor_id === this.store.subdistributor_id &&
          chain.distributor_id === this.store.distributor_id
      );
    },

    selectedChain() {
      return this.chains.find(c => c.id === this.store.chain_id);
    },

    validUsername() {
      if (!this.store.username) return true;
      if (this.store.id && this.store.username === this.store.id) return true;
      if (isNaN(this.store.username)) return true;
      return false;
    },
  },
  created() {
    this.refreshData();
  },

  methods: {
    refreshData() {
      $api.get('/v1/dashboard/distributors').then(distributors => {
        this.distributors = distributors.data;
      });
      $api.get('/v1/dashboard/subdistributors').then(subdistributors => {
        this.subdistributors = subdistributors.data;
      });
      $api.get('/v1/dashboard/chains').then(chains => {
        this.chains = chains.data;
      });
      $api.get('/v1/common/countries').then(countries => {
        this.countries = countries;
      });

      if (this.$route.query.id) {
        $api.get(`/v1/dashboard/stores/${this.$route.query.id}`).then(store => {
          this.originalName = store.name;
          store.username = store.username || store.id;
          this.store = store;
        });
      } else {
        this.store = {
          distributor_id:
            parseInt(this.$route.query.distributor_id, 10) ||
            $auth.user.selectedAccess.distributor_id,
          subdistributor_id:
            parseInt(this.$route.query.subdistributor_id, 10) ||
            $auth.user.selectedAccess.subdistributor_id,
          chain_id:
            parseInt(this.$route.query.chain_id, 10) ||
            $auth.user.selectedAccess.chain_id,
          active: true,
        };
      }
    },
    validateForm() {
      this.errors = [];
      if (!this.store.distributor_id) {
        this.errors.push(
          this.getLabel('FORM_ERROR_DISTRIBUTOR_REQUIRED') ||
            'The distributor field is required.'
        );
      }

      if (!this.store.chain_id) {
        this.errors.push(
          this.getLabel('FORM_ERROR_CHAIN_REQUIRED') ||
            'The chain field is required.'
        );
      }

      if (!this.store.name) {
        this.errors.push(
          this.getLabel('FORM_ERROR_NAME_REQUIRED') ||
            'The name field is required.'
        );
      }

      if (!this.validUsername) {
        this.errors.push(
          this.getLabel('FORM_ERROR_INVALID_USERNAME') ||
            'The username is invalid.  It must be the store ID OR contain letters'
        );
      }

      if (!this.store.password) {
        this.errors.push(
          this.getLabel('FORM_ERROR_STORE_PASSWORD_REQUIRED') ||
            'The store password field is required.'
        );
      }
    },
    save() {
      if (!this.$refs.editorForm.checkValidity()) {
        this.$refs.editorForm.reportValidity();
        this.validateForm();
        return;
      }

      this.errors = [];
      if (this.store.id) {
        $api
          .put(`/v1/dashboard/stores/${this.store.id}`, this.store)
          .then(store => {
            this.$router.push({
              name: 'store-detail',
              params: store,
            });
          })
          .catch(e => {
            alert(`${this.getLabel('FORM_ERROR_SAVING')}: ${e}`);
          });
      } else {
        this.store.credits_available = 5;
        $api
          .post('/v1/dashboard/stores', this.store)
          .then(store => {
            this.$router.push({
              name: 'store-detail',
              params: store,
            });
          })
          .catch(e => {
            alert(`${this.getLabel('FORM_ERROR_SAVING')}: ${e}`);
          });
      }
    },
  },
};
</script>
